import type { Model } from '../../types/mfc'
import { inChatQuickBuyDialog } from '../actions/inChatQuickBuyDialog'
import { pubSub } from '../app-state/pubSub'
import { EventType, UserEvent } from '../constants/enums'
import { _fetchTokenConfig } from '../mfcLogic/api/tokens'
import { pubSubUsers } from './pubSub'
import { AppRouter } from './state.router'
import { SETTINGSSTATE } from './state.settings'

const serverSideUsers: { [id: number]: Model } = {
    486121: {
        blurb: 'A place for Premium Members to hang out.',
        id: 486121,
        level: 4,
        name: 'Lounge',
        slug: 'lounge',
    },
    486123: {
        id: 486123,
        slug: 'lounge1000',
        level: 4,
    },
    486124: {
        id: 486124,
        name: 'Lounge10000',
        slug: 'lounge10000',
        level: 4,
    },
    37512: {
        id: 37512,
        name: 'MyFreeCams',
        level: 5,
    },
}
const serverSideSessionToID: { [session: number]: number } = {}
const serverSideSlugToID: { [slug: string]: number } = {
    lounge: 486121,
    lounge1000: 486123,
    lounge10000: 486124,
    MyFreeCams: 37512,
}
const serverSideIdToSlug: { [id: number]: string } = {
    486121: 'lounge',
    486123: 'lounge1000',
    486124: 'lounge10000',
    37512: 'MyFreeCams',
}
const serverSideIdToName: { [id: number]: string } = {}

if (typeof window !== 'undefined' && window.__MFC_SERVER_SIDE_STATE__) {
    window.__MFC_SERVER_SIDE_STATE__.forEach((state) => {
        const { type, data } = state

        if (type !== EventType.SERVER_SIDE_USER) {
            return
        }

        const { user, isMe } = data as { user: Model; isMe?: boolean }
        const { session, slug, name, id } = user

        if (isMe) {
            serverSideSlugToID['me'] = id
            serverSideIdToSlug[id] = 'me'
        }

        if (slug) {
            serverSideSlugToID[slug] = id
            serverSideIdToSlug[id] = slug
        }

        if (session) {
            serverSideSessionToID[session] = id
        }

        if (name) {
            serverSideIdToName[id] = name
        }

        serverSideUsers[id] = { ...user }
    })
}

/**
 * __Front-end user state__.
 * Source of truth is the worker user state, `state.users`.
 * Updates are applied to `USERSSTATE` as needed via `onChanges`.
 *
 * *** DO NOT ACCESS THIS DIRECTLY ***
 *
 * Use `const user = await loadUserIfNeeded(identifier)` to access this state.
 */
export const USERSSTATE = serverSideUsers
export const sessionToID = serverSideSessionToID
export const slugToID = serverSideSlugToID
export const idToSlug = serverSideIdToSlug
export const idToName = serverSideIdToName

if (typeof window !== 'undefined') {
    window.__MFC_APP_USERS__ = {
        idToName,
        idToSlug,
        slugToID,
        sessionToID,
        users: USERSSTATE,
    }
}

export const resolveID = (identifier: string | number) =>
    typeof identifier === 'number'
        ? identifier
        : typeof identifier === 'string'
          ? slugToID[identifier?.toLowerCase()]
          : undefined

export const resolveSlug = (identifier: string | number) =>
    typeof identifier === 'string'
        ? idToSlug[identifier] || identifier?.toLowerCase()
        : typeof identifier === 'number'
          ? idToSlug[identifier]
          : undefined

export const getSlugAndId = (
    slugOrId: string | number,
): [slug: string, id: number] => {
    return [resolveSlug(slugOrId), resolveID(slugOrId)] as [
        slug: string,
        id: number,
    ]
}

export const getServerCachedUser = (slugOrId: string | number): Model => {
    // Relevant to server side only!
    if (!global?.__APP_LOOKUP_USER_CACHE__?.[slugOrId]) {
        return
    }
    return global?.__APP_LOOKUP_USER_CACHE__[slugOrId].result
}

export const getStateUser = (id: number) => USERSSTATE[id]

export const getUser = (identifier: string | number) =>
    getStateUser(resolveID(identifier)) ||
    getServerCachedUser(resolveID(identifier)) ||
    getServerCachedUser(resolveSlug(identifier))

export const meUser = new Promise((resolve) => {
    const me = USERSSTATE[slugToID['me']]
    if (me && me.id) return resolve(true)

    pubSubUsers.on(UserEvent.ME_ID, () => {
        resolve(true)
    })
    setTimeout(() => {
        // ensure we eventually return this if this event isnt coming
        resolve(false)
    }, 10000)
})

pubSub.USERS.on(
    'UPDATE:tokens',
    ({ current: tokens }: { current: number; id: number }) => {
        const { disableInChatQuickbuy } = SETTINGSSTATE.MY_ACCOUNT

        if (tokens !== 0 || disableInChatQuickbuy) {
            return
        }

        const {
            visibleQuery: { slug: querySlug } = {},
            floatingPage,
            visiblePage,
            slug,
        } = AppRouter.route

        if (
            (slug || querySlug) &&
            (visiblePage === 'room' || visiblePage === 'message')
        ) {
            _fetchTokenConfig().then((config) => {
                inChatQuickBuyDialog({
                    ...config,
                    location: {
                        type: floatingPage.substring(1),
                        slug: slug.toString(),
                    },
                })
            })
        }
    },
)
